// Screen Wake Lock — keeps a player's phone awake during active play so it
// doesn't sleep mid-night-phase (audit P3-8).
//
// HONEST LIMITATION: `navigator.wakeLock` exists only in secure contexts
// (HTTPS or localhost). The primary deployment — plain HTTP over LAN — is a
// non-secure origin, so the API is simply undefined there and this module
// no-ops gracefully. It DOES work for localhost dev and any TLS deployment;
// for plain-LAN parties the README tells players to bump their screen-sleep
// timeout instead.

import type { GamePhase } from '../../shared/src/types.js';

export type ShellRole = 'idle' | 'host' | 'player';

// Phases where a player is staring at the phone waiting on (or hiding) a
// decision and a sleeping screen hurts: the whole night, and all of the day.
// Lobby / role-reveal / game-over are actively-tapped moments, so the OS
// idle timer resets itself there anyway.
const HOLD_PHASES: ReadonlySet<GamePhase> = new Set([
  'night',
  'day-discussion',
  'day-vote',
  'day-result',
]);

// Pure decision (unit-tested): hold the lock only for seated PLAYERS during
// active play. The host device is excluded on purpose — it's typically a
// laptop running the game-master view, and burning a phone-host's battery
// after they deliberately set the device down is their call, not ours.
export function shouldHoldWakeLock(
  role: ShellRole,
  phase: GamePhase | null | undefined,
): boolean {
  return role === 'player' && phase != null && HOLD_PHASES.has(phase);
}

// Acquire a screen wake lock; returns a cleanup that releases it. Safe to
// call anywhere: feature-detected, never throws. The UA auto-releases the
// lock whenever the tab is hidden (app switch, screen manually turned off),
// so we reacquire on visibilitychange for as long as the cleanup hasn't run.
export function acquireWakeLock(): () => void {
  // lib.dom types `navigator.wakeLock` as always-present; the cast keeps the
  // runtime feature-detect honest (undefined on non-secure origins — see the
  // module comment — and on older browsers).
  const wakeLock = (navigator as Navigator & { wakeLock?: WakeLock }).wakeLock;
  if (!wakeLock || typeof wakeLock.request !== 'function') {
    return () => {};
  }

  let sentinel: WakeLockSentinel | null = null;
  let released = false;

  const request = async () => {
    try {
      const s = await wakeLock.request('screen');
      if (released) {
        // Cleanup ran while the request was in flight — don't leak the lock.
        s.release().catch(() => {});
        return;
      }
      sentinel = s;
    } catch {
      // Denied (battery saver, permissions policy) — nothing actionable; the
      // game works fine, the screen just dims on the OS schedule.
    }
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') void request();
  };
  document.addEventListener('visibilitychange', onVisibility);
  void request();

  return () => {
    released = true;
    document.removeEventListener('visibilitychange', onVisibility);
    sentinel?.release().catch(() => {});
    sentinel = null;
  };
}
